import { useCallback, useState } from "react";
import { useDropzone, type FileRejection } from "react-dropzone";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";

const ACCEPTED_TYPES = {
  "application/pdf": [".pdf"],
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [
    ".docx",
  ],
  "text/plain": [".txt"],
  "text/markdown": [".md"],
  "text/csv": [".csv"],
};

interface FileUploadProps {
  onUpload: (file: File) => void;
  isUploading?: boolean;
}

export function FileUpload({ onUpload, isUploading }: FileUploadProps) {
  const [rejectMsg, setRejectMsg] = useState("");

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      setRejectMsg("");
      acceptedFiles.forEach((file) => onUpload(file));
    },
    [onUpload],
  );

  const onDropRejected = useCallback((rejections: FileRejection[]) => {
    const reasons = rejections
      .flatMap((r) => r.errors.map((e) => e.message))
      .join("; ");
    setRejectMsg(reasons || "File not accepted");
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    onDropRejected,
    accept: ACCEPTED_TYPES,
    maxSize: 100 * 1024 * 1024, // 100MB
    disabled: isUploading,
  });

  return (
    <Box
      {...getRootProps()}
      sx={{
        border: "2px dashed",
        borderColor: isDragActive ? "primary.main" : "divider",
        borderRadius: 2,
        p: 3,
        textAlign: "center",
        cursor: isUploading ? "not-allowed" : "pointer",
        bgcolor: isDragActive ? "action.hover" : "transparent",
        opacity: isUploading ? 0.5 : 1,
        transition: "all 0.2s",
        "&:hover": {
          borderColor: "primary.main",
          bgcolor: "action.hover",
        },
      }}
    >
      <input {...getInputProps()} />
      <CloudUploadIcon sx={{ fontSize: 36, color: "text.disabled", mb: 1 }} />
      <Typography variant="body2" sx={{
        color: "text.secondary"
      }}>
        {isDragActive
          ? "Drop files here..."
          : isUploading
            ? "Uploading..."
            : "Drag & drop files here, or click to select"}
      </Typography>
      <Typography variant="caption" sx={{
        color: "text.disabled"
      }}>
        PDF, DOCX, TXT, MD, CSV (max 100MB)
      </Typography>
      {rejectMsg && (
        <Typography variant="caption" color="error" sx={{ mt: 1 }}>
          {rejectMsg}
        </Typography>
      )}
    </Box>
  );
}
